import React, { useEffect, useState } from 'react';
import { Block } from '../types';
import { LogoType, useLogoStore } from './store';
import { LogoFrame } from './logo-frame';
import { animated, easings, useTransition } from '@react-spring/web';
import { FLOAT_ANIMATION_CONFIG } from 'src/document-editor/neue-bubble-menu-fixed';
import { Nullable } from 'src/types/nullable.type';
import { PickLogoPanel } from './pick-logo-panel';
import { useBlockActions } from '../helpers/actions/use-block-actions.hook';
import { useGetActiveLogoStack } from './get-active-logo-stack.hook';

type Props = {
  block: Block;
};

export const AddLogoControlPanelContent = ({ block }: Props) => {
  const [activeLogoPickerPanel, setActiveLogoPickerPanel] = useState<Nullable<LogoType>>(null);
  const setDisplayLogo = useLogoStore((state) => state.setDisplayLogo);
  const { onLogoBlockUpdated } = useBlockActions(block);
  const {
    selectedOrgLogo,
    selectedRecipientLogo,
    orgLogoEnabled,
    recipientLogoEnabled,
    availableRecipientLogos,
    customRecipientLogos,
    availableOrgLogos,
    customOrgLogos,
  } = useGetActiveLogoStack(block);

  const data = activeLogoPickerPanel ? [activeLogoPickerPanel] : [];

  const [transitions, logoPickerTransitionApi] = useTransition<LogoType, { y: number; opacity: number }>(data, () => ({
    key: (type: LogoType) => type,
    from: () => {
      return {
        y: 8,
        opacity: 0,
      };
    },
    enter: () => {
      return {
        y: 0,
        opacity: 1,
        config: FLOAT_ANIMATION_CONFIG,
      };
    },
    leave: () => {
      return {
        y: 0,
        opacity: 0,
        config: { duration: 200, easing: easings.easeOutCubic },
      };
    },
  }));

  useEffect(() => {
    logoPickerTransitionApi.start();
  }, [activeLogoPickerPanel]);

  return (
    <>
      {transitions((style) => (
        <animated.div className='absolute top-0 right-[248px] mr-2' style={style}>
          <PickLogoPanel type={activeLogoPickerPanel} block={block} />
        </animated.div>
      ))}
      <div className='flex flex-col gap-4'>
        <span className='text-neue-journey-small -mt-3'>Personalize your Journey with logos</span>
        <LogoFrame
          title='Organization'
          src={selectedOrgLogo}
          enabled={orgLogoEnabled}
          logoCount={availableOrgLogos.length + customOrgLogos.length}
          onToggle={(value) => {
            setDisplayLogo(block.id, 'org', value);
            onLogoBlockUpdated({ orgLogoEnabled: value });
          }}
          onLogoClick={() => {
            setActiveLogoPickerPanel('org');
          }}
        />
        <LogoFrame
          title='Recipient'
          src={selectedRecipientLogo}
          logoCount={availableRecipientLogos.length + customRecipientLogos.length}
          enabled={recipientLogoEnabled}
          onToggle={(value) => {
            onLogoBlockUpdated({ recipientLogoEnabled: value });
            setDisplayLogo(block.id, 'recipient', value);
          }}
          onLogoClick={() => {
            setActiveLogoPickerPanel('recipient');
          }}
        />
      </div>
    </>
  );
};
